import React, { useState, useEffect } from 'react';
import './Pages.css';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPolicies, setClientPolicies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Create client states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submittingClient, setSubmittingClient] = useState(false);
  const [newClient, setNewClient] = useState({
    firstName: '',
    lastName: '',
    surname: '',
    email: '',
    phone: '',
    kraPin: '',
    idNumber: '',
    password: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/customers');
      const data = await response.json();

      if (data.success) {
        setClients(data.customers);
      } else {
        setError('Failed to load clients');
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleViewClient = async (clientEmail) => {
    setModalLoading(true);
    setShowModal(true);
    
    try {
      const response = await fetch(`https://main.victriedemos.com:3017/api/customers/${clientEmail}`);
      const data = await response.json();

      if (data.success) {
        setSelectedClient(data.customer);
        setClientPolicies(data.policies);
      } else {
        alert('Failed to load client details');
      }
    } catch (err) {
      console.error('Error fetching client details:', err);
      alert('Unable to load client details');
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedClient(null);
    setClientPolicies([]);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    if (!newClient.firstName || !newClient.lastName || !newClient.email || !newClient.phone || !newClient.idNumber) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newClient.email)) {
      alert('Please enter a valid email address');
      return;
    }
    
    setSubmittingClient(true);
    
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newClient)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Client created successfully!');
        setShowCreateModal(false);
        // Reset form
        setNewClient({
          firstName: '',
          lastName: '',
          surname: '',
          email: '',
          phone: '',
          kraPin: '',
          idNumber: '',
          password: ''
        });
        fetchClients(); // Refresh the list
      } else {
        alert(data.message || 'Failed to create client');
      }
    } catch (err) {
      console.error('Error creating client:', err);
      alert('Unable to create client');
    } finally {
      setSubmittingClient(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const getStatusClass = (status) => {
    if (status === 1 || status === '1' || status === 'Active') return 'active';
    if (status === 2 || status === '2' || status === 'Expired') return 'inactive';
    if (status === 3 || status === '3') return 'pending';
    if (status === 4 || status === '4') return 'inactive';
    return 'pending';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Clients</h1>
        </div>
        <div className="loading">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Client Management</h1>
          <p className="page-description">Manage customer information and policies</p>
        </div>
        <button className="primary-btn" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-plus-circle"></i> New Client
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>ID Number</th>
              <th>KRA PIN</th>
              <th>Status</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">No clients found</td>
              </tr>
            ) : (
              clients.map((client, index) => (
                <tr key={client.Email}>
                  <td>{index + 1}</td>
                  <td className="client-name">{client.FullName || `${client.FirstName} ${client.LastName}`}</td>
                  <td>{client.Email}</td>
                  <td>{client.Phone || 'N/A'}</td>
                  <td>{client.IDNumber || 'N/A'}</td>
                  <td>{client.KraPin || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${client.Active === 1 ? 'active' : 'inactive'}`}>
                      {client.Active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatDate(client.CreatedAt)}</td>
                  <td className="action-buttons">
                    <button 
                      className="btn-view"
                      onClick={() => handleViewClient(client.Email)}
                      title="View Client"
                    >
                      <i className="bi bi-eye"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Client Details Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Client Details</h2>
              <button className="close-button" onClick={closeModal}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="modal-body">
              {modalLoading ? (
                <div className="loading">Loading client details...</div>
              ) : selectedClient ? (
                <>
                  {/* Client Information */}
                  <div className="detail-section">
                    <h3>Client Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>First Name:</label>
                        <span>{selectedClient.FirstName || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Last Name:</label>
                        <span>{selectedClient.LastName || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Surname:</label>
                        <span>{selectedClient.Surname || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Email:</label>
                        <span>{selectedClient.Email || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Phone:</label>
                        <span>{selectedClient.Phone || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>ID Number:</label>
                        <span>{selectedClient.IDNumber || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>KRA PIN:</label>
                        <span>{selectedClient.KraPin || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <label>Status:</label>
                        <span className={`status-badge ${selectedClient.Active === 1 ? 'active' : 'inactive'}`}>
                          {selectedClient.Active === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Created At:</label>
                        <span>{formatDate(selectedClient.CreatedAt)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Logged In:</label>
                        <span className={`status-badge ${selectedClient.IsLoggedin === 1 ? 'active' : 'inactive'}`}>
                          {selectedClient.IsLoggedin === 1 ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Client Policies */}
                  <div className="detail-section" style={{ marginTop: '20px' }}>
                    <h3>Client Policies ({clientPolicies.length})</h3>
                    {clientPolicies.length === 0 ? (
                      <p className="no-data">No policies found for this client</p>
                    ) : (
                      <div className="table-container" style={{ maxHeight: '400px', overflow: 'auto' }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Policy Number</th>
                              <th>Product</th>
                              <th>Registration</th>
                              <th>Sum Insured</th>
                              <th>Premium</th>
                              <th>Start Date</th>
                              <th>End Date</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientPolicies.map((policy, index) => (
                              <tr key={policy.Id}>
                                <td>{index + 1}</td>
                                <td className="policy-number">{policy.PolicyNumber}</td>
                                <td>{policy.ProductName}</td>
                                <td>{policy.Registration || 'N/A'}</td>
                                <td className="amount">{formatCurrency(policy.SumInsured)}</td>
                                <td className="amount">{formatCurrency(policy.GrossPremium)}</td>
                                <td>{formatDate(policy.CommencementDate)}</td>
                                <td>{formatDate(policy.ExpiryDate)}</td>
                                <td>
                                  <span className={`status-badge ${getStatusClass(policy.Status)}`}>
                                    {policy.StatusText || 'N/A'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="error-message">Failed to load client details</div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeModal} className="btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create Client Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Client</h2>
              <button className="close-button" onClick={() => setShowCreateModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <form onSubmit={handleCreateClient}>
              <div className="modal-body">
                <div className="detail-section">
                  <h3>Client Information</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>First Name <span className="required">*</span></label>
                      <input
                        type="text"
                        value={newClient.firstName}
                        onChange={(e) => setNewClient({...newClient, firstName: e.target.value})}
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Last Name <span className="required">*</span></label>
                      <input
                        type="text"
                        value={newClient.lastName}
                        onChange={(e) => setNewClient({...newClient, lastName: e.target.value})}
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Surname</label>
                      <input
                        type="text"
                        value={newClient.surname}
                        onChange={(e) => setNewClient({...newClient, surname: e.target.value})}
                        placeholder="Enter surname (optional)"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Email <span className="required">*</span></label>
                      <input
                        type="email"
                        value={newClient.email}
                        onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Phone <span className="required">*</span></label>
                      <input
                        type="tel"
                        value={newClient.phone}
                        onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                        placeholder="Enter phone number"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>ID Number <span className="required">*</span></label>
                      <input
                        type="text"
                        value={newClient.idNumber}
                        onChange={(e) => setNewClient({...newClient, idNumber: e.target.value})}
                        placeholder="Enter ID number"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>KRA PIN</label>
                      <input
                        type="text"
                        value={newClient.kraPin}
                        onChange={(e) => setNewClient({...newClient, kraPin: e.target.value})}
                        placeholder="Enter KRA PIN (optional)"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Password</label>
                      <input
                        type="password"
                        value={newClient.password}
                        onChange={(e) => setNewClient({...newClient, password: e.target.value})}
                        placeholder="Enter password (optional)"
                      />
                    </div>
                  </div>
                  
                  <div className="info-box" style={{ marginTop: '15px', padding: '12px', background: '#e3f2fd', borderRadius: '8px', fontSize: '13px', color: '#1976d2' }}>
                    <i className="bi bi-info-circle"></i> <strong>Note:</strong> The system will check if Email, KRA PIN, or ID Number already exists before creating the client. Client will be created with Active status.
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)} 
                  className="btn-secondary"
                  disabled={submittingClient}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary-btn"
                  disabled={submittingClient}
                >
                  {submittingClient ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
